import React, { useState, useMemo, useCallback, useEffect } from 'react';
import AsyncSelect from 'react-select/async';
import { MultiValue, StylesConfig } from 'react-select';
import { carService, printService } from '../services';
import { Car } from '../types';
import { Layout } from '../components/layout';
import { formatDateOrToday } from '../utils/tableUtils';
import BookingConfirmation from './BookingConfirmation';

interface Option {
    value: string;
    label: string;
    car: Car;
}

const BookingConfirmationPage: React.FC = () => {
    const [selectedCars, setSelectedCars] = useState<Car[]>([]);
    const [generating, setGenerating] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [includeRelated, setIncludeRelated] = useState(true);

    useEffect(() => {
        const checkDarkMode = () => {
            setIsDarkMode(document.documentElement.classList.contains('dark'));
        };
        checkDarkMode();
        const observer = new MutationObserver(checkDarkMode);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });
        return () => observer.disconnect();
    }, []);

    const loadOptions = useCallback(async (inputValue: string): Promise<Option[]> => {
        if (!inputValue.trim()) return [];
        
        try {
            const results = await carService.search(inputValue.trim(), 'chassis_number', { limit: 50 });
            return (results as Car[]).map(car => {
                const makeModel = car.make?.name && car.model?.name
                    ? `${car.make.name} ${car.model.name}`
                    : (car.manual_make_model || `${car.make?.name || ''}${car.model?.name || ''}`).trim();
                return {
                    value: car.id,
                    label: `${car.chassis_number} - ${makeModel}`.trim(),
                    car
                };
            });
        } catch (error) {
            console.error('Error searching cars:', error);
            return [];
        }
    }, []);

    const handleChange = (newValue: MultiValue<Option>) => {
        setSelectedCars(newValue.map(opt => opt.car));
    };

    const selectedOptions = useMemo(() => {
        return selectedCars.map(car => {
            const makeModel = car.make?.name && car.model?.name
                ? `${car.make.name} ${car.model.name}`
                : (car.manual_make_model || `${car.make?.name || ''}${car.model?.name || ''}`).trim();
            return {
                value: car.id,
                label: `${car.chassis_number} - ${makeModel}`.trim(),
                car
            };
        });
    }, [selectedCars]);

    // Get first car's booking info for display
    const firstCar = selectedCars[0];
    const bookingNumber = firstCar?.booking_reference || '';
    // Use original_booking for loading port info (terminal, address, POL)
    // original_booking is the booking voyage that has the loading port (POL) information
    const originalBooking = firstCar?.original_booking;
    
    // Get POL port data from original_booking (the booking port = loading port)
    // These fields come from the booking voyage's port relationship
    const polTerminal = originalBooking?.port_terminal || '';
    const polAddress = originalBooking?.port_address || '';
    const polName = originalBooking?.port_name || '';
    const polRemarks = originalBooking?.port_remarks || '';

    // Build the 9-row car entries - destination comes from car's port (destination port)
    // Related cars are displayed in the same row as the main unit
    const carEntries = useMemo(() => {
        const entries: Array<{
            nr: number;
            description: string;
            vinSerial: string;
            destination: string;
            relatedCars?: Array<{
                relationshipType: string;
                makeModel: string;
                vinSerial: string;
            }>;
        }> = [];

        for (const car of selectedCars) {
            if (entries.length >= 9) break;

            const dbMake = car.make?.name || '';
            const dbModel = car.model?.name || '';
            // Use database make/model if available, otherwise use manual_make_model
            const makeModel = dbMake && dbModel
                ? `${dbMake} ${dbModel}`
                : (dbMake || dbModel || car.manual_make_model || '');

            // Build related cars array if includeRelated is true
            let relatedCarsInfo: Array<{
                relationshipType: string;
                makeModel: string;
                vinSerial: string;
            }> | undefined;

            if (includeRelated && car.related_cars && car.related_cars.length > 0) {
                // Sort: Attached first, then STC
                const sortedRelatedCars = [...car.related_cars].sort((a, b) => {
                    if (a.relationship_type === 'Attached' && b.relationship_type !== 'Attached') return -1;
                    if (a.relationship_type !== 'Attached' && b.relationship_type === 'Attached') return 1;
                    return 0;
                });

                relatedCarsInfo = sortedRelatedCars.map(rc => {
                    const rcDbMake = rc.make?.name || '';
                    const rcDbModel = rc.model?.name || '';
                    // Use database make/model if available, otherwise use manual_make_model
                    const rcMakeModel = rcDbMake && rcDbModel
                        ? `${rcDbMake} ${rcDbModel}`
                        : (rcDbMake || rcDbModel || rc.manual_make_model || '-');
                    return {
                        relationshipType: rc.relationship_type || 'STC',
                        makeModel: rcMakeModel.trim() || '-',
                        vinSerial: rc.chassis_number
                    };
                });
            }

            // Destination comes from car.port (the car's destination port)
            entries.push({
                nr: entries.length + 1,
                description: makeModel.trim() || '-',
                vinSerial: car.chassis_number,
                destination: car.port?.port || car.port?.code || '',
                relatedCars: relatedCarsInfo
            });
        }

        // Fill remaining slots with empty entries
        while (entries.length < 9) {
            entries.push({
                nr: entries.length + 1,
                description: '',
                vinSerial: '',
                destination: ''
            });
        }

        return entries;
    }, [selectedCars, includeRelated]);

    // POL comes from original_booking (loading port)
    const pol = polName;

    const generatePDF = async () => {
        if (selectedCars.length === 0) return;

        setGenerating(true);
        // Title format: Booking Confirmation + POL + booking number
        const filename = `Booking Confirmation ${pol || 'Unknown'} ${bookingNumber || 'No Booking'}.pdf`;

        try {
            // Terminal, address, POL come from original_booking (the booking voyage's loading port)
            await printService.generateBookingConfirmation({
                bookingNumber: bookingNumber || undefined,
                terminal: polTerminal,
                date: formatDateOrToday(firstCar?.created_at),
                pol: pol,
                terminalAddress: polAddress,
                cars: carEntries,
                portRemarks: polRemarks || undefined
            }, filename);
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setGenerating(false);
        }
    };

    const customStyles: StylesConfig<Option, true> = {
        control: (base, state) => ({
            ...base,
            minHeight: '38px',
            maxHeight: '78px',
            borderWidth: '1px',
            borderColor: state.isFocused 
                ? '#3b82f6' 
                : isDarkMode 
                ? '#4b5563' 
                : '#d1d5db',
            borderRadius: '8px',
            backgroundColor: isDarkMode ? '#374151' : 'white',
            boxShadow: state.isFocused 
                ? `0 0 0 2px ${isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)'}` 
                : 'none',
            overflow: 'hidden',
            '&:hover': {
                borderColor: state.isFocused 
                    ? '#3b82f6' 
                    : isDarkMode 
                    ? '#6b7280' 
                    : '#9ca3af'
            }
        }),
        valueContainer: (base) => ({
            ...base,
            maxHeight: '72px',
            overflowY: 'auto',
            overflowX: 'hidden',
            flexWrap: 'wrap',
            gap: '4px'
        }),
        placeholder: (base) => ({
            ...base,
            color: isDarkMode ? '#9ca3af' : '#6b7280',
            fontSize: '14px'
        }),
        input: (base) => ({
            ...base,
            color: isDarkMode ? '#f9fafb' : '#111827',
            fontSize: '14px',
            margin: '0',
            padding: '0'
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: isDarkMode ? '#1f2937' : '#eff6ff',
            borderRadius: '6px',
            margin: '2px'
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: isDarkMode ? '#f9fafb' : '#1e40af',
            fontSize: '14px',
            padding: '2px 6px'
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: isDarkMode ? '#f9fafb' : '#1e40af',
            borderRadius: '0 6px 6px 0',
            '&:hover': {
                backgroundColor: '#dc2626',
                color: 'white'
            }
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '8px',
            backgroundColor: isDarkMode ? '#1f2937' : 'white',
            boxShadow: isDarkMode
                ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            overflow: 'hidden'
        }),
        menuList: (base) => ({
            ...base,
            padding: 0,
            maxHeight: '300px',
            overflowY: 'auto'
        }),
        option: (base, state) => ({
            ...base,
            fontSize: '14px',
            padding: '10px 12px',
            backgroundColor: state.isSelected
                ? '#3b82f6'
                : state.isFocused
                ? isDarkMode
                    ? '#374151'
                    : '#eff6ff'
                : isDarkMode
                ? '#1f2937'
                : 'white',
            color: state.isSelected 
                ? 'white' 
                : isDarkMode 
                ? '#f9fafb' 
                : '#111827',
            borderBottom: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
            '&:last-child': {
                borderBottom: 'none'
            },
            '&:active': {
                backgroundColor: state.isSelected ? '#3b82f6' : isDarkMode ? '#4b5563' : '#dbeafe'
            }
        })
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                        Booking Confirmation
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                        Search by chassis number and select cars to generate booking confirmation
                    </p>

                    <div className="flex gap-4 items-center flex-wrap">
                        <div className="w-96">
                            <AsyncSelect<Option, true>
                                isMulti
                                cacheOptions
                                defaultOptions={false}
                                loadOptions={loadOptions}
                                onChange={handleChange}
                                value={selectedOptions}
                                placeholder="Type chassis number to search..."
                                noOptionsMessage={({ inputValue }) => 
                                    inputValue ? 'No cars found' : 'Type to search...'
                                }
                                loadingMessage={() => 'Searching...'}
                                styles={customStyles}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                closeMenuOnSelect={false}
                            />
                        </div>

                        <label className="flex items-center gap-2 cursor-pointer px-4 py-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <input
                                type="checkbox"
                                checked={includeRelated}
                                onChange={(e) => setIncludeRelated(e.target.checked)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">Include Related</span>
                        </label>

                        {selectedCars.length > 0 && (
                            <button
                                onClick={generatePDF}
                                disabled={generating}
                                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors"
                            >
                                {generating ? 'Generating...' : 'Save as PDF'}
                            </button>
                        )}
                    </div>
                </div>

                {selectedCars.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-600">Preview</span>
                            <span className="text-sm text-gray-500">{selectedCars.length} car(s) selected</span>
                        </div>
                        <div className="p-4">
                            <BookingConfirmation
                                bookingNumber={bookingNumber || undefined}
                                terminal={polTerminal}
                                date={formatDateOrToday(firstCar?.created_at)}
                                pol={pol}
                                terminalAddress={polAddress}
                                cars={carEntries}
                                portRemarks={polRemarks || undefined}
                            />
                        </div>
                    </div>
                )}

                {selectedCars.length === 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
                        <div className="text-gray-400 dark:text-gray-500">
                            <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p>Type a chassis number to search and select cars</p>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default BookingConfirmationPage;
